import {
  ChangeEvent,
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  IConsolePageInputsProps,
  ISelectOption,
} from "pages/[tenant]/console/types";

import { InputSelectContainer, OptionsList } from "./styles";

export type IConsolePageInputExtendedProps =
  InputHTMLAttributes<HTMLInputElement> & IConsolePageInputsProps;

interface ConsolePageInputSelectProps extends IConsolePageInputExtendedProps {
  options: ISelectOption[];
  label: string;
  value: string;
  required?: boolean;
  isInvalid?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const ConsolePageInputSelect = ({
  options,
  label,
  value,
  required,
  isInvalid,
  placeholder,
  disabled = false,
  ...props
}: ConsolePageInputSelectProps) => {
  const [filteredOptions, setFilteredOptions] = useState<ISelectOption[]>(
    options || [],
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e?.target?.value;
      props?.onChange(e);

      const filtered = options.filter((opt) =>
        opt?.label?.toLowerCase()?.includes(val.toLowerCase()),
      );
      setFilteredOptions(filtered);
      setDropdownVisible(true);
    },
    [options, props],
  );

  const handleSelectOption = useCallback(
    (option: ISelectOption) => {
      const syntheticEvent = {
        target: { value: option?.label },
      } as ChangeEvent<HTMLInputElement>;

      props?.onChange(syntheticEvent);
      setDropdownVisible(false);
    },
    [props],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef?.current &&
        !containerRef?.current.contains(event.target as Node)
      ) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <InputSelectContainer
      shouldMoveFloatingLabel={!!value}
      isInvalid={isInvalid}
      ref={containerRef}
    >
      <label className="label">
        {label} {required && <span className="required-flag">*</span>}
      </label>

      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={() => setDropdownVisible(true)}
        placeholder={placeholder}
        disabled={disabled}
        className={isInvalid ? "input invalid" : "input"}
      />

      {dropdownVisible && filteredOptions?.length > 0 && (
        <OptionsList>
          {filteredOptions?.map((option) => (
            <li key={option?.id} onClick={() => handleSelectOption(option)}>
              {option?.label}
            </li>
          ))}
        </OptionsList>
      )}
    </InputSelectContainer>
  );
};
